import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Code, Briefcase, Mail, FileText, Award, BookOpen, GripVertical } from 'lucide-react';
import { Button } from './ui/button';

// Import Window Components
import AboutWindow from './windows/AboutWindow';
import SkillsWindow from './windows/SkillsWindow';
import ProjectsWindow from './windows/ProjectsWindow';
import ContactWindow from './windows/ContactWindow';
import ResumeWindow from './windows/ResumeWindow';
import AchievementsWindow from './windows/AchievementsWindow';
import BlogWindow from './windows/BlogWindow';
import Terminal from './windows/TerminalWindow';

export const CentralHub = ({ expanded, onToggle, onOpenWindow, windows, onRestoreWindow }) => {
  const [isDragging, setIsDragging] = useState(false);
  
  // Updated sections with IDs and Content components
  const sections = [
    { 
      id: 'about',
      icon: User, 
      label: 'About', 
      color: 'text-primary',
      content: <AboutWindow />
    },
    { 
      id: 'skills',
      icon: Code, 
      label: 'Skills', 
      color: 'text-accent',
      content: <SkillsWindow />
    },
    { 
      id: 'projects',
      icon: Briefcase, 
      label: 'Projects', 
      color: 'text-primary',
      content: <ProjectsWindow />
    },
    { 
      id: 'contact',
      icon: Mail, 
      label: 'Contact', 
      color: 'text-accent',
      content: <ContactWindow />
    },
    { 
      id: 'resume',
      icon: FileText, 
      label: 'Resume', 
      color: 'text-primary',
      content: <ResumeWindow />
    },
    { 
      id: 'achievements',
      icon: Award, 
      label: 'Achievements', 
      color: 'text-accent',
      content: <AchievementsWindow />
    },
    { 
      id: 'blog',
      icon: BookOpen, 
      label: 'Blog', 
      color: 'text-primary',
      content: <BlogWindow />
    },
      { 
      id: 'blog',
      icon: BookOpen, 
      label: 'Blog', 
      color: 'text-primary',
      content: <Terminal />
    },
  ];

  const handleAppClick = (section) => {
    const isOpen = windows.some(w => w.appId === section.id);
    const minimizedWindow = windows.find(w => w.appId === section.id && w.minimized);

    if (minimizedWindow) {
      onRestoreWindow(minimizedWindow.id);
    } else if (!isOpen) {
      onOpenWindow(section.id, section.label, section.content);
    }
    
    // Optional: Close the hub automatically after opening an app
    onToggle(); 
  };

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
      {/* Central Arc Reactor Hub */}
      <motion.div
        className="relative cursor-pointer"
        onClick={onToggle}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Outer Ring */}
        <motion.div
          className="absolute inset-0 w-48 h-48 rounded-full border-2 border-primary/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
        
        {/* Middle Ring */}
        <motion.div
          className="absolute inset-4 rounded-full border-2 border-accent/30"
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
        />
        
        {/* Inner Ring */}
        <motion.div
          className="absolute inset-8 rounded-full border border-primary/40"
          animate={{ rotate: 360 }}
          transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
        />

        {/* Core */}
        <div className="relative w-48 h-48 flex items-center justify-center">
          <motion.div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-primary via-primary-glow to-accent"
            animate={{
              boxShadow: [
                '0 0 20px hsl(200 100% 55% / 0.5), 0 0 40px hsl(200 100% 55% / 0.3)',
                '0 0 40px hsl(200 100% 55% / 0.7), 0 0 60px hsl(200 100% 55% / 0.4)',
                '0 0 20px hsl(200 100% 55% / 0.5), 0 0 40px hsl(200 100% 55% / 0.3)',
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-full h-full rounded-full bg-background/20 backdrop-blur-sm flex items-center justify-center">
              <motion.span 
                className="text-2xl font-bold glow-text"
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                MELON
              </motion.span>
            </div>
          </motion.div>
        </div>

        {/* Data Rings */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute inset-0 rounded-full border border-primary/10"
            style={{ scale: 1 + (i * 0.15) }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1 + (i * 0.15), 1.2 + (i * 0.15), 1 + (i * 0.15)]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3
            }}
          />
        ))}
      </motion.div>

      {/* Expanded Info Panel - Draggable with Framer Motion */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            drag
            dragMomentum={false}
            dragElastic={0.1}
            dragConstraints={{ top: -300, left: -700, right: 700, bottom: 300 }}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            initial={{ opacity: 0, scale: 0.8, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed w-[600px] z-[100]"
            style={{ 
              top: '50%',
              left: '50%',
              marginTop: '-200px',
              marginLeft: '-300px',
            }}
          >
            <div className={`glass-panel tech-border rounded-xl p-8 space-y-6 relative transition-shadow cursor-move ${isDragging ? 'shadow-2xl shadow-primary/30 scale-[1.02]' : ''}`}>
              {/* Drag Handle Indicator */}
              <div className="absolute top-2 right-2 p-2 bg-primary/5 rounded-lg group pointer-events-none">
                <GripVertical className={`w-5 h-5 text-muted-foreground group-hover:text-primary ${!isDragging ? 'animate-pulse' : ''}`} />
              </div>

              {/* Header */}
              <div className="text-center space-y-2 pr-10">
                <h2 className="text-3xl font-bold glow-text">MELON AI v2.0</h2>
                <p className="text-muted-foreground">Personal Operating System AI</p>
                <p className="text-xs text-primary/80 animate-pulse"></p>
              </div>

                {/* Quick Access */}
                <div className="grid grid-cols-4 gap-4">
                  {sections.map((section, i) => (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Button
                        variant="outline"
                        className="w-full h-20 flex flex-col gap-2 tech-border hover:bg-primary/10 transition-all cursor-pointer"
                        onClick={() => handleAppClick(section)}
                      >
                        <section.icon className={`w-6 h-6 ${section.color}`} />
                        <span className="text-xs">{section.label}</span>
                      </Button>
                    </motion.div>
                  ))}
                </div>

                {/* Status */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">12</div>
                    <div className="text-xs text-muted-foreground">PROJECTS</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-accent">98%</div>
                    <div className="text-xs text-muted-foreground">EFFICIENCY</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success">ONLINE</div>
                    <div className="text-xs text-muted-foreground">STATUS</div>
                  </div>
                </div>
              </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CentralHub;
